type SoundName =
  | "ui"
  | "jump"
  | "stomp"
  | "coin"
  | "bump"
  | "break"
  | "powerup"
  | "pipe"
  | "death"
  | "flag"
  | "fireball"
  | "pause"
  | "kick"
  | "oneup"
  | "clear"
  | "gameover"
  | "axe"
  | "hurt"
  | "item";

export type MusicTheme = "overworld" | "underground" | "athletic" | "castle" | "hurry" | "none";

/** Looping stage tracks (user-supplied, authorized). */
export type MusicTrack = Exclude<MusicTheme, "hurry" | "none"> | "star";
/** One-shot jingles that replace synthesized cues when available. */
export type JingleName = "clear" | "pipe" | "death" | "gameover";
export type AudioClip = MusicTrack | JingleName;

export const MUSIC_TRACK_FILES: Record<MusicTrack, string> = {
  overworld: "assets/audio/ground-theme.mp3",
  underground: "assets/audio/underground-theme.mp3",
  athletic: "assets/audio/underwater-theme.mp3",
  castle: "assets/audio/castle-theme.mp3",
  star: "assets/audio/invincibility-theme.mp3",
};

export const JINGLE_FILES: Record<JingleName, string> = {
  clear: "assets/audio/level-complete.mp3",
  pipe: "assets/audio/into-the-pipe.mp3",
  death: "assets/audio/lost-a-life.mp3",
  gameover: "assets/audio/game-over.mp3",
};

export const AUDIO_CLIP_FILES: Record<AudioClip, string> = {
  ...MUSIC_TRACK_FILES,
  ...JINGLE_FILES,
};

/** Loop points (seconds) for tracks whose files carry a silent tail. */
const LOOP_END: Partial<Record<MusicTrack, number>> = {
  star: 14.8,
};

/** How long a jingle keeps stage music silent, in seconds (defaults to full length). */
const JINGLE_HOLD: Partial<Record<JingleName, number>> = {
  pipe: 1.1,
};

export interface MusicState {
  theme: MusicTheme;
  track: MusicTrack | undefined;
  hurry: boolean;
  star: boolean;
  paused: boolean;
  jingle: JingleName | undefined;
  /** "track" when an MP3 is playing, "synth" when the procedural fallback is used. */
  source: "track" | "synth" | "silent";
}

const AUDIO_KEY = "super-quint-bros-audio";
const MUSIC_LEVEL = 0.16;
const TRACK_LEVEL = 0.34;
const JINGLE_LEVEL = 0.4;
const HURRY_RATE = 1.28;

interface PlayingTrack {
  track: MusicTrack;
  source: AudioBufferSourceNode;
  gain: GainNode;
  startedAt: number;
  offset: number;
}

interface PlayingJingle {
  name: JingleName;
  source: AudioBufferSourceNode;
  gain: GainNode;
  holdUntil: number;
}

const JINGLES: ReadonlySet<string> = new Set<JingleName>(["clear", "pipe", "death", "gameover"]);

class SynthAudio {
  private context?: AudioContext;
  private master?: GainNode;
  private musicGain?: GainNode;
  private effectsGain?: GainNode;
  private musicTimer?: number;
  private beat = 0;
  private enabled = true;
  private theme: MusicTheme = "overworld";
  private hurry = false;
  private star = false;
  private paused = false;
  private readonly pendingClips = new Map<AudioClip, ArrayBuffer>();
  private readonly buffers = new Map<AudioClip, AudioBuffer>();
  private readonly failedClips = new Set<AudioClip>();
  private readonly offsets = new Map<MusicTrack, number>();
  private decoding?: Promise<void>;
  private playing?: PlayingTrack;
  private jingle?: PlayingJingle;
  private holdTimer?: number;

  constructor() {
    try {
      this.enabled = window.localStorage.getItem(AUDIO_KEY) !== "off";
    } catch {
      this.enabled = true;
    }
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  get musicState(): MusicState {
    return {
      theme: this.theme,
      track: this.wantedTrack(),
      hurry: this.hurry,
      star: this.star,
      paused: this.paused,
      jingle: this.jingle?.name,
      source: this.playing
        ? "track"
        : this.theme === "none" || !this.enabled || !this.context || this.paused || this.jingle
          ? "silent"
          : "synth",
    };
  }

  /** Decoded length of a clip in seconds, or undefined when the synth fallback is in use. */
  clipDuration(clip: AudioClip): number | undefined {
    return this.buffers.get(clip)?.duration;
  }

  /**
   * Hand over raw MP3 bytes (preloaded by the Boot scene). Decoding waits for
   * the AudioContext, which browsers only allow after a user gesture.
   */
  registerClip(clip: AudioClip, data: ArrayBuffer): void {
    if (this.buffers.has(clip)) return;
    this.pendingClips.set(clip, data);
    if (this.context) void this.decodePending();
  }

  async unlock(): Promise<void> {
    if (!this.context) {
      const Context = window.AudioContext ?? window.webkitAudioContext;
      if (!Context) return;
      this.context = new Context();
      this.master = this.context.createGain();
      this.musicGain = this.context.createGain();
      this.effectsGain = this.context.createGain();
      this.master.gain.value = this.enabled ? 0.7 : 0;
      this.musicGain.gain.value = MUSIC_LEVEL;
      this.effectsGain.gain.value = 0.32;
      this.musicGain.connect(this.master);
      this.effectsGain.connect(this.master);
      this.master.connect(this.context.destination);
    }
    if (this.context.state === "suspended") await this.context.resume();
    this.startMusic();
    await this.decodePending();
    this.syncTrack();
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    try {
      window.localStorage.setItem(AUDIO_KEY, this.enabled ? "on" : "off");
    } catch {
      // ignore
    }
    if (this.context && this.master) {
      this.master.gain.setTargetAtTime(this.enabled ? 0.7 : 0, this.context.currentTime, 0.02);
    }
    if (this.enabled) void this.unlock();
    else this.stopTrack(true);
    return this.enabled;
  }

  setTheme(theme: MusicTheme): void {
    if (theme === "hurry") {
      // Hurry is a modifier on the current stage theme, like the 1985 speed-up.
      if (!this.hurry) {
        this.hurry = true;
        this.beat = 0;
        this.syncTrack();
      }
      return;
    }
    const changed = theme !== this.theme;
    this.hurry = false;
    this.star = false;
    this.theme = theme;
    this.beat = 0;
    if (theme !== "none") {
      this.paused = false;
      // Stage themes restart from the top whenever a level (re)starts.
      if (changed) this.offsets.delete(theme);
      else this.offsets.set(theme, 0);
    }
    this.syncTrack(true);
    if (this.context && this.musicGain && theme !== "none") {
      this.musicGain.gain.setTargetAtTime(MUSIC_LEVEL, this.context.currentTime, 0.05);
    }
  }

  /** Swap in the invincibility theme while the star is active; resumes the stage track after. */
  setStar(active: boolean): void {
    if (this.star === active) return;
    this.star = active;
    if (active) this.offsets.set("star", 0);
    this.syncTrack();
  }

  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    if (paused) {
      this.stopTrack(true);
    } else if (this.theme !== "none") {
      this.syncTrack();
    }
    if (!this.context || !this.musicGain) return;
    this.musicGain.gain.setTargetAtTime(
      paused ? 0.0001 : MUSIC_LEVEL,
      this.context.currentTime,
      0.08,
    );
  }

  private wantedTrack(): MusicTrack | undefined {
    if (this.theme === "none" || this.theme === "hurry") return undefined;
    return this.star ? "star" : this.theme;
  }

  private async decodePending(): Promise<void> {
    if (this.decoding) return this.decoding;
    const context = this.context;
    if (!context || this.pendingClips.size === 0) return;
    this.decoding = (async () => {
      for (const [clip, data] of [...this.pendingClips.entries()]) {
        this.pendingClips.delete(clip);
        try {
          const buffer = await context.decodeAudioData(data.slice(0));
          this.buffers.set(clip, buffer);
        } catch (error) {
          this.failedClips.add(clip);
          console.warn(`Audio clip "${clip}" failed to decode; using synth fallback.`, error);
        }
      }
    })().finally(() => {
      this.decoding = undefined;
    });
    await this.decoding;
    if (this.pendingClips.size > 0) await this.decodePending();
    else this.syncTrack();
  }

  /** Ensure exactly the right track (or none) is playing for the current state. */
  private syncTrack(restart = false): void {
    const track = this.wantedTrack();
    const jingleHold = this.jingle !== undefined && this.jingle.holdUntil > (this.context?.currentTime ?? 0);
    const wanted =
      this.enabled && !this.paused && !document.hidden && !jingleHold && track
        ? track
        : undefined;
    if (!wanted) {
      this.stopTrack(true);
      return;
    }
    const rate = this.hurry && wanted !== "star" ? HURRY_RATE : 1;
    if (this.playing && this.playing.track === wanted && !restart) {
      this.playing.source.playbackRate.setValueAtTime(rate, this.context?.currentTime ?? 0);
      return;
    }
    const buffer = this.buffers.get(wanted);
    if (!buffer || !this.context || !this.musicGain) {
      this.stopTrack(true);
      return;
    }
    this.stopTrack(true);
    const loopEnd = LOOP_END[wanted] ?? buffer.duration;
    const offset = (this.offsets.get(wanted) ?? 0) % loopEnd;
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = buffer;
    source.loop = true;
    source.loopStart = 0;
    source.loopEnd = loopEnd;
    source.playbackRate.value = rate;
    gain.gain.value = TRACK_LEVEL / MUSIC_LEVEL;
    source.connect(gain);
    gain.connect(this.musicGain);
    source.start(0, offset);
    this.playing = {
      track: wanted,
      source,
      gain,
      startedAt: this.context.currentTime,
      offset,
    };
  }

  private stopTrack(remember: boolean): void {
    const playing = this.playing;
    if (!playing || !this.context) {
      this.playing = undefined;
      return;
    }
    const elapsed =
      (this.context.currentTime - playing.startedAt) * playing.source.playbackRate.value;
    const loopEnd = LOOP_END[playing.track] ?? playing.source.buffer?.duration ?? 1;
    this.offsets.set(playing.track, remember ? (playing.offset + elapsed) % loopEnd : 0);
    try {
      playing.source.stop();
    } catch {
      // already stopped
    }
    playing.source.disconnect();
    playing.gain.disconnect();
    this.playing = undefined;
  }

  /** Play a one-shot jingle; stage music pauses for its hold window. Returns false when unavailable. */
  private playJingle(name: JingleName): boolean {
    const buffer = this.buffers.get(name);
    if (!buffer || !this.context || !this.master || !this.enabled) return false;
    this.stopJingle();
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = buffer;
    gain.gain.value = JINGLE_LEVEL;
    source.connect(gain);
    gain.connect(this.master);
    const hold = JINGLE_HOLD[name] ?? buffer.duration;
    const jingle: PlayingJingle = {
      name,
      source,
      gain,
      holdUntil: this.context.currentTime + hold,
    };
    this.jingle = jingle;
    source.onended = () => {
      if (this.jingle === jingle) this.jingle = undefined;
      source.disconnect();
      gain.disconnect();
      this.syncTrack();
    };
    source.start();
    // Music is silenced while the jingle plays and resumes when the hold expires.
    this.stopTrack(true);
    if (this.holdTimer !== undefined) window.clearTimeout(this.holdTimer);
    this.holdTimer = window.setTimeout(() => {
      this.holdTimer = undefined;
      this.syncTrack();
    }, hold * 1000 + 20);
    return true;
  }

  private stopJingle(): void {
    const jingle = this.jingle;
    if (!jingle) return;
    this.jingle = undefined;
    jingle.source.onended = null;
    try {
      jingle.source.stop();
    } catch {
      // already stopped
    }
    jingle.source.disconnect();
    jingle.gain.disconnect();
  }

  play(name: SoundName): void {
    if (!this.enabled || !this.context || !this.effectsGain) return;
    if (JINGLES.has(name) && this.playJingle(name as JingleName)) return;
    const now = this.context.currentTime;
    switch (name) {
      case "jump":
        this.tone(420, 680, 0.12, "square", 0.07, now);
        break;
      case "stomp":
        this.tone(180, 90, 0.08, "square", 0.08, now);
        break;
      case "coin":
        this.tone(980, 1320, 0.09, "square", 0.06, now);
        this.tone(1320, 1560, 0.1, "square", 0.05, now + 0.08);
        break;
      case "bump":
        this.tone(90, 70, 0.08, "square", 0.08, now);
        break;
      case "break":
        this.noise(0.12, 0.1, now);
        this.tone(200, 80, 0.12, "sawtooth", 0.06, now);
        break;
      case "powerup":
        [0, 0.07, 0.14, 0.21].forEach((delay, i) => {
          this.tone(360 + i * 140, 480 + i * 160, 0.1, "square", 0.06, now + delay);
        });
        break;
      case "pipe":
        this.tone(240, 90, 0.28, "square", 0.07, now);
        break;
      case "death":
        this.tone(320, 80, 0.7, "square", 0.09, now);
        this.tone(240, 60, 0.5, "square", 0.06, now + 0.2);
        break;
      case "flag":
        [0, 0.1, 0.2, 0.32, 0.46].forEach((delay, i) => {
          this.tone(392 * (1 + i * 0.12), 440 * (1 + i * 0.12), 0.18, "square", 0.06, now + delay);
        });
        break;
      case "fireball":
        this.tone(240, 520, 0.08, "sawtooth", 0.05, now);
        break;
      case "pause":
        this.tone(520, 520, 0.06, "square", 0.05, now);
        this.tone(390, 390, 0.06, "square", 0.05, now + 0.08);
        break;
      case "kick":
        this.tone(140, 280, 0.07, "square", 0.07, now);
        break;
      case "oneup":
        [523, 659, 784, 1046].forEach((freq, i) => {
          this.tone(freq, freq, 0.12, "square", 0.07, now + i * 0.11);
        });
        break;
      case "clear":
        [0, 0.14, 0.28, 0.46, 0.68].forEach((delay, i) => {
          this.tone(330 + i * 90, 360 + i * 90, 0.2, "square", 0.07, now + delay);
        });
        break;
      case "gameover":
        this.tone(196, 130, 0.9, "square", 0.08, now);
        this.tone(164, 98, 0.8, "square", 0.07, now + 0.35);
        break;
      case "axe":
        this.tone(180, 90, 0.2, "sawtooth", 0.08, now);
        this.noise(0.2, 0.08, now);
        break;
      case "hurt":
        this.tone(200, 90, 0.22, "square", 0.08, now);
        break;
      case "item":
        this.tone(520, 780, 0.12, "square", 0.06, now);
        break;
      case "ui":
        this.tone(360, 540, 0.06, "square", 0.05, now);
        break;
    }
  }

  private startMusic(): void {
    if (this.musicTimer !== undefined) return;
    this.musicTimer = window.setInterval(() => this.musicBeat(), 180);
  }

  private musicBeat(): void {
    if (
      !this.enabled ||
      !this.context ||
      !this.musicGain ||
      this.paused ||
      document.hidden ||
      this.playing
    ) {
      return;
    }
    if (this.theme === "none" || this.theme === "hurry") return;
    // A decoded track may have arrived since the last beat; prefer it.
    if (this.buffers.has(this.theme)) {
      this.syncTrack();
      if (this.playing) return;
    }
    const now = this.context.currentTime;
    const patterns: Record<Exclude<MusicTheme, "none">, number[]> = {
      overworld: [196, 246, 293, 329, 293, 246, 220, 196],
      underground: [98, 98, 110, 87, 98, 130, 110, 87],
      athletic: [261, 329, 392, 329, 293, 246, 261, 196],
      castle: [110, 116, 103, 98, 110, 82, 87, 98],
      hurry: [329, 392, 440, 392, 349, 329, 293, 261],
    };
    const bass: Record<Exclude<MusicTheme, "none">, number[]> = {
      overworld: [98, 98, 87, 82, 98, 110, 87, 73],
      underground: [49, 49, 55, 43, 49, 65, 55, 43],
      athletic: [130, 130, 146, 110, 130, 164, 146, 98],
      castle: [41, 43, 36, 32, 41, 49, 36, 32],
      hurry: [146, 146, 164, 130, 146, 174, 164, 130],
    };
    const pattern: Exclude<MusicTheme, "none" | "hurry"> | "hurry" = this.hurry
      ? "hurry"
      : this.theme;
    const lead = patterns[pattern];
    const low = bass[pattern];
    const step = this.beat % lead.length;
    if (step % 2 === 0) this.musicTone(low[step]!, low[step]! * 0.96, 0.14, "triangle", 0.11, now);
    this.musicTone(lead[step]!, lead[step]! * 1.01, 0.08, "square", 0.035, now);
    if (step === 2 || step === 6) {
      this.musicTone(
        lead[step]! * 1.5,
        lead[step]! * 1.48,
        0.1,
        "square",
        0.018,
        now + 0.02,
      );
    }
    if (step === 0 || step === 4) {
      this.musicPulse(now);
    }
    this.beat += 1;
  }

  private musicPulse(startTime: number): void {
    if (!this.context || !this.musicGain) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(72, startTime);
    oscillator.frequency.exponentialRampToValueAtTime(42, startTime + 0.1);
    gain.gain.setValueAtTime(0.06, startTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.11);
    oscillator.connect(gain);
    gain.connect(this.musicGain);
    oscillator.start(startTime);
    oscillator.stop(startTime + 0.12);
  }

  private tone(
    startFrequency: number,
    endFrequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    startTime: number,
  ): void {
    if (!this.effectsGain) return;
    this.scheduleTone(this.effectsGain, startFrequency, endFrequency, duration, type, volume, startTime);
  }

  private musicTone(
    startFrequency: number,
    endFrequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    startTime: number,
  ): void {
    if (!this.musicGain) return;
    this.scheduleTone(this.musicGain, startFrequency, endFrequency, duration, type, volume, startTime);
  }

  private scheduleTone(
    destination: AudioNode,
    startFrequency: number,
    endFrequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    startTime: number,
  ): void {
    if (!this.context) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(startFrequency, startTime);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), startTime + duration);
    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(volume, startTime + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
    oscillator.connect(gain);
    gain.connect(destination);
    oscillator.start(startTime);
    oscillator.stop(startTime + duration + 0.02);
  }

  private noise(duration: number, volume: number, startTime: number): void {
    if (!this.context || !this.effectsGain) return;
    const frameCount = Math.max(1, Math.floor(this.context.sampleRate * duration));
    const buffer = this.context.createBuffer(1, frameCount, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < frameCount; i += 1) data[i] = Math.random() * 2 - 1;
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    source.buffer = buffer;
    filter.type = "lowpass";
    filter.frequency.value = 1400;
    gain.gain.setValueAtTime(volume, startTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.effectsGain);
    source.start(startTime);
  }
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

export const synthAudio = new SynthAudio();
